/**
 * Agent + AgentVersion request/response shapes.
 *
 * Endpoints (per IMPLEMENTATION.md §9.1):
 *   POST   /api/v1/agents              → CreateAgentRequest  → AgentResponse
 *   GET    /api/v1/agents              → paginated AgentResponse
 *   GET    /api/v1/agents/:id          → AgentResponse
 *   PATCH  /api/v1/agents/:id          → UpdateAgentRequest  → AgentVersionResponse (creates a new version)
 *   GET    /api/v1/agents/:id/versions → paginated AgentVersionResponse
 *
 * `AgentConfig` is a discriminated union over `backend` (matches `BackendId`
 * in @simpliwork/executor-core §4.1). Phase 1 only ships `anthropic_managed`
 * and `worker`; the others are scaffolded so the schema doesn't need a
 * breaking change when they land.
 */
import { z } from "zod";
import { IsoTimestampSchema, UuidSchema } from "./common.js";
// ─── BackendId & per-backend AgentConfig ─────────────────────────────────────
/**
 * The set of executor backends the system supports. Matches
 * `@simpliwork/executor-core`'s `BackendId` union exactly:
 *   - `anthropic_messages` — Phase-1 stand-in (RLY-017). Calls the Anthropic
 *     Messages API directly via `client.messages.create(...)`.
 *   - `anthropic_managed` — RESERVED for the Phase-2 executor that wraps
 *     Anthropic's Managed Agents API once it is GA in our account.
 *   - `worker`, `fly_machine`, `agents_sdk` — additional Phase-2+ backends.
 *
 * RLY-021 (Sprint 06) added `anthropic_messages` to this enum so the
 * agents-create endpoint can persist agent_versions rows that the Phase-1
 * orchestrator (`apps/orchestrator/src/functions/run-agent.ts:405`)
 * consumes by literal-string backend match.
 */
export const BackendIdSchema = z.enum([
    "anthropic_messages",
    "anthropic_managed",
    "openai_responses",
    "worker",
    "fly_machine",
    "agents_sdk",
]);
const SecretsRefsSchema = z
    .array(z.string().min(1).max(64))
    .max(32)
    .describe("Secret names this agent is allowed to read at run time");
const McpBindingDeclSchema = z.object({
    instance_id: UuidSchema,
    allowed_tools: z.array(z.string().min(1)).max(64),
    scope: z.record(z.unknown()).default({}),
});
/**
 * `allowed_egress` — Phase-1 forward-compat hook for Phase-5 egress enforcement
 * (RLY-036, ARCHITECTURE.md §13/§14).
 *
 * Phase-1 default: the literal string `["*"]` — wildcard, all egress permitted.
 * No Phase-1 backend ENFORCES this list; it is persisted into `agent_versions.config`
 * so that when Phase-5 turns on enforcement, every legacy agent already has an
 * explicit (even if permissive) value rather than a missing field.
 *
 * Phase-5 rollout plan: see `docs/runbooks/egress-enforcement-rollout.md`.
 * Briefly: enforcement defaults to allow-all for 30 days, then default switches
 * to `[]` with a tenant-side opt-out for legacy agents during the transition.
 *
 * The schema accepts an array of host patterns (RFC 1035 hostnames or wildcards
 * starting with `*.`); the Phase-1 default value `["*"]` is the single
 * universal-wildcard form. We do not validate that the entries are
 * well-formed hosts in Phase 1 — Phase-5 enforcement adds that validation.
 */
const AllowedEgressSchema = z
    .array(z.string().min(1).max(253))
    .max(256)
    .default(["*"])
    .describe("Host allow-list for outbound network egress from the agent runtime. " +
    "Phase-1 default `['*']` (wildcard); enforcement lands in Phase 5.");
/**
 * Phase-1 Anthropic Messages config (RLY-017 / RLY-021). Shipped in
 * Sprint 06 as the first concrete agent config the portal API can persist.
 * The orchestrator's `defaultExecutorForBackend` at
 * `apps/orchestrator/src/functions/run-agent.ts:405` literal-matches the
 * `backend` field, so this variant is the only one accepted at run dispatch
 * time today.
 */
const AnthropicMessagesConfigSchema = z.object({
    backend: z.literal("anthropic_messages"),
    model: z.string().min(1),
    system_prompt: z.string().min(1).max(20_000),
    secrets_refs: SecretsRefsSchema.default([]),
    mcp_bindings: z.array(McpBindingDeclSchema).default([]),
    allowed_egress: AllowedEgressSchema,
});
const AnthropicManagedConfigSchema = z.object({
    backend: z.literal("anthropic_managed"),
    model: z.string().min(1),
    system_prompt: z.string().min(1).max(20_000),
    secrets_refs: SecretsRefsSchema.default([]),
    mcp_bindings: z.array(McpBindingDeclSchema).default([]),
    allowed_egress: AllowedEgressSchema,
});
const WorkerConfigSchema = z.object({
    backend: z.literal("worker"),
    entry: z.string().min(1).describe("Worker script entry path or service name"),
    model: z.string().min(1),
    system_prompt: z.string().min(1).max(20_000),
    secrets_refs: SecretsRefsSchema.default([]),
    mcp_bindings: z.array(McpBindingDeclSchema).default([]),
    allowed_egress: AllowedEgressSchema,
});
const FlyMachineConfigSchema = z.object({
    backend: z.literal("fly_machine"),
    image: z.string().min(1),
    cpu: z.number().int().min(1).max(8).default(1),
    memory_mb: z.number().int().min(256).max(8192).default(512),
    region: z.string().length(3).optional(),
    secrets_refs: SecretsRefsSchema.default([]),
    mcp_bindings: z.array(McpBindingDeclSchema).default([]),
    allowed_egress: AllowedEgressSchema,
});
const AgentsSdkConfigSchema = z.object({
    backend: z.literal("agents_sdk"),
    agent_id: z.string().min(1),
    model: z.string().min(1),
    secrets_refs: SecretsRefsSchema.default([]),
    mcp_bindings: z.array(McpBindingDeclSchema).default([]),
    allowed_egress: AllowedEgressSchema,
});
/**
 * OpenAI Responses-API config (M1.1, 2026-05-08). Mirrors
 * `AnthropicMessagesConfigSchema`. The `OPENAI_API_KEY` is an operator secret
 * read from Worker env; tenant `secrets_refs` are kept on the schema for
 * symmetry but are NOT used to provide the LLM API key.
 *
 * Server-side validation of `model` against the curated registry (`./models.ts`
 * `RegistryModelIdSchema`) is enforced at the create/update handler boundary,
 * not on the persisted-shape level — that lets existing rows continue to load
 * if a model is later removed from the registry.
 */
const OpenAIResponsesConfigSchema = z.object({
    backend: z.literal("openai_responses"),
    model: z.string().min(1),
    system_prompt: z.string().min(1).max(20_000),
    secrets_refs: SecretsRefsSchema.default([]),
    mcp_bindings: z.array(McpBindingDeclSchema).default([]),
    allowed_egress: AllowedEgressSchema,
});
/**
 * Discriminated union — the `backend` literal selects which fields are valid.
 * AC for RLY-015 explicitly calls this out as a Zod discriminated union.
 * RLY-021 (Sprint 06) added `AnthropicMessagesConfigSchema` as the Phase-1
 * variant. M1.1 (2026-05-08) added `OpenAIResponsesConfigSchema` alongside.
 */
export const AgentConfigSchema = z.discriminatedUnion("backend", [
    AnthropicMessagesConfigSchema,
    AnthropicManagedConfigSchema,
    OpenAIResponsesConfigSchema,
    WorkerConfigSchema,
    FlyMachineConfigSchema,
    AgentsSdkConfigSchema,
]);
// ─── Resource limits (per AgentRun in executor-core) ─────────────────────────
export const ResourceLimitsSchema = z.object({
    timeout_seconds: z.number().int().min(1).max(86_400),
    max_tokens: z.number().int().min(1).max(1_000_000),
    max_cost_micros: z.number().int().min(0),
});
// ─── CRUD shapes ─────────────────────────────────────────────────────────────
export const CreateAgentRequestSchema = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(2_000).optional(),
    config: AgentConfigSchema,
    limits: ResourceLimitsSchema,
});
export const UpdateAgentRequestSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(2_000).optional(),
    config: AgentConfigSchema.optional(),
    limits: ResourceLimitsSchema.optional(),
})
    .refine((v) => v.name !== undefined ||
    v.description !== undefined ||
    v.config !== undefined ||
    v.limits !== undefined, { message: "At least one updatable field is required" });
export const AgentResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    current_version: z.number().int().min(1),
    backend: BackendIdSchema,
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const AgentVersionResponseSchema = z.object({
    agent_id: UuidSchema,
    version: z.number().int().min(1),
    backend: BackendIdSchema,
    config: AgentConfigSchema,
    limits: ResourceLimitsSchema,
    created_at: IsoTimestampSchema,
    created_by: z.string().describe("Actor identifier, e.g. `user:<uuid>`"),
});
/**
 * Compact response from `POST /api/v1/agents` per RLY-021 AC #1:
 * "Returns `201 { agent_id, version: 1 }`."
 */
export const CreateAgentResponseSchema = z.object({
    agent_id: UuidSchema,
    version: z.literal(1).describe("Initial version always 1"),
});
/**
 * Compact response from `PATCH /api/v1/agents/:id` per RLY-021 AC #4:
 * "Returns `200 { version: N }`."
 */
export const UpdateAgentResponseSchema = z.object({
    version: z.number().int().min(2).describe("New version (always >= 2; PATCH never creates v1)"),
});
/**
 * Detail response from `GET /api/v1/agents/:id` per RLY-021 AC #3:
 * "Returns full detail including `current_version` config."
 *
 * The bare `AgentResponseSchema` carries `current_version: number` (the
 * integer pointer); the detail response replaces it with the full
 * `current_version_config` object resolved from `agent_versions`.
 */
export const AgentDetailResponseSchema = AgentResponseSchema.extend({
    current_version_config: AgentVersionResponseSchema.describe("The agent_versions row for `agents.current_version`"),
});
//# sourceMappingURL=agents.js.map